// only the parts of BankAccountDepositOrWithdrawForm that are rendering the form
import React, { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { ChangeCashMode } from './model';
import { MoneyInput, Stack, Typography } from '@pflegenavi/web-components';
import type { CashListIconComponent } from '@pflegenavi/web-components';
import { MAX_BANK_ACCOUNT_CHANGE } from '@pflegenavi/shared/constants';
import { Button, FormHelperText } from '@mui/material';
import euroSign from '../../assets/cash_list.svg';
import { SkipCashSelectionButton } from './atoms/SkipCashSelectionButton';
import type { BankAccountDepositOrWithdrawFormProps } from './BankAccountForm';
import { ChangeSummaryCard } from './cards/ChangeSummaryCard';

type BankAccountDepositOrWithdrawFormViewProps = {
  bankAccountChange?: number;
  setBankAccountChange: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  handleSwitchToCash?: VoidFunction;
  totalBankBalance: number;
  newAccountingPaymentsFeatureFlagEnabled?: boolean;
  bankOnly?: boolean;
  cashListName?: string;
  cashListIcon?: CashListIconComponent;
  // Mirrors the cash list's allowManualNegativeBankAccountAmount flag. When true, a
  // withdrawal may drive the bank balance below zero. Defaults to permissive so callers
  // that don't know about the flag keep the pre-existing behaviour.
  allowNegativeBankAccountAmount?: boolean;
} & Pick<
  BankAccountDepositOrWithdrawFormProps,
  | 'allowSkipCashSelection'
  | 'setSkippedCashSelection'
  | 'requiredAmount'
  | 'mode'
  | 'nextStep'
  | 'handleClose'
>;
// Only the parts of BankAccountDepositOrWithdrawForm that are rendering the form
export const BankAccountDepositOrWithdrawFormView: FC<
  BankAccountDepositOrWithdrawFormViewProps
> = ({
  bankAccountChange,
  mode,
  setBankAccountChange,
  requiredAmount,
  handleSwitchToCash,
  totalBankBalance,
  allowSkipCashSelection,
  setSkippedCashSelection,
  nextStep,
  handleClose,
  newAccountingPaymentsFeatureFlagEnabled,
  bankOnly,
  cashListName,
  cashListIcon,
  allowNegativeBankAccountAmount = true,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents, fNumber } = useFormatting();

  const isDeposit = mode === ChangeCashMode.Deposit;
  const amountLabel = bankOnly
    ? t('common.amount')
    : t(`cashManagement.${isDeposit ? 'deposit' : 'withdrawal'}-amount`);

  const changeFactor = useMemo(() => {
    if (isDeposit) {
      return 1;
    }
    return -1;
  }, [isDeposit]);

  const requiredAmountSet =
    requiredAmount === undefined || requiredAmount === bankAccountChange;

  const newBalanceNegative =
    !isDeposit &&
    !allowNegativeBankAccountAmount &&
    totalBankBalance - (bankAccountChange ?? 0) < 0;

  const confirmButtonDisabled = useMemo(() => {
    const noChange = bankAccountChange === 0 || bankAccountChange === undefined;
    return noChange || !requiredAmountSet || newBalanceNegative;
  }, [bankAccountChange, requiredAmountSet, newBalanceNegative]);

  const moneyInputHelperText = useMemo(() => {
    if (!requiredAmountSet) {
      return t('cashManagement.bank-account-change-amount-error', {
        amount: fCurrencyCents(requiredAmount),
      });
    }
    return undefined;
  }, [fCurrencyCents, requiredAmount, requiredAmountSet, t]);

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
        }}
      >
        {bankOnly
          ? t(`common.${isDeposit ? 'deposit' : 'withdrawal'}`)
          : t('cashManagement.total-account')}
      </Typography>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 3,
        }}
      >
        <MoneyInput
          label={amountLabel}
          placeholder={fNumber(0, 2)}
          value={bankAccountChange}
          maxValue={MAX_BANK_ACCOUNT_CHANGE}
          onChange={(newValue) => {
            setBankAccountChange(newValue);
          }}
          helperText={
            moneyInputHelperText && (
              <FormHelperText sx={{ m: 0 }} error>
                {moneyInputHelperText}
              </FormHelperText>
            )
          }
          slotProps={{
            htmlInput: { maxLength: 7 },
            inputLabel: { shrink: true },
          }}
        />
        <Stack
          sx={{
            gap: 1,
          }}
        >
          {cashListName && cashListIcon && (
            <ChangeSummaryCard
              mode={mode}
              cashListName={cashListName}
              Icon={cashListIcon}
              currentBalance={totalBankBalance}
              amount={changeFactor * (bankAccountChange ?? 0)}
              hideHeader
            />
          )}
          {newBalanceNegative && (
            <Typography
              variant="caption"
              color="error"
              sx={{
                textAlign: 'right',
              }}
            >
              {t('cashManagement.bank-account-new-balance-negative-error')}
            </Typography>
          )}
        </Stack>
      </Stack>
      <Stack>
        <Stack
          direction="row"
          sx={{
            justifyContent: newAccountingPaymentsFeatureFlagEnabled
              ? 'flex-end'
              : 'space-between',

            alignItems: 'center',
          }}
        >
          {!newAccountingPaymentsFeatureFlagEnabled && (
            <Button
              sx={{
                visibility: handleSwitchToCash ? undefined : 'hidden',
              }}
              onClick={handleSwitchToCash}
              startIcon={
                <img
                  style={{
                    width: 20,
                    marginTop: -2,
                  }}
                  alt=""
                  src={euroSign}
                />
              }
            >
              {t(
                `cashManagement.${
                  isDeposit ? 'deposit-to' : 'withdraw-from'
                }-cash`
              )}
            </Button>
          )}

          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button variant="outlined" onClick={handleClose}>
              {t('cancel')}
            </Button>
            <Button
              data-cy="cash-mgmt-button"
              variant="contained"
              disabled={confirmButtonDisabled}
              onClick={nextStep}
            >
              {t('actions.confirm')}
            </Button>
          </Stack>
        </Stack>
        {allowSkipCashSelection && (
          <Stack
            direction="row"
            sx={{
              justifyContent: 'flex-start',
            }}
          >
            <SkipCashSelectionButton
              disabled={newBalanceNegative}
              onClick={() => {
                setSkippedCashSelection(true);
                nextStep();
              }}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
